import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LogOut, ArrowLeft } from "lucide-react";
import { API_ROOT } from "@/config";
import SEO from "@/components/seo";

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const SelectionResult = () => {
  const { selection_id } = useParams<{ selection_id: string }>();
  const navigate = useNavigate();
  const [selection, setSelection] = useState<any>(null);
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);

  // Get the token from localStorage
  const token = localStorage.getItem("token");
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  // Fetch presigned URL for direct S3 access (better performance)
  useEffect(() => {
    const fetchVideoUrl = async () => {
      if (!selection?.unique_id || !token) return;
      
      try {
        // Try to get presigned URL for direct S3 access
        const response = await fetch(`${API_ROOT}/video/${selection.unique_id}/url?token=${token}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.presigned_url) {
            console.log("✅ SELECTION: Got presigned URL for direct S3 access");
            setVideoUrl(data.presigned_url);
          } else if (data.local_available) {
            console.log("📁 SELECTION: Video available locally, using proxied access");
            setVideoUrl(`${API_ROOT}/video/${selection.unique_id}?token=${token}`);
          }
        } else {
          console.warn("⚠️ SELECTION: Failed to get presigned URL, falling back to proxied access");
          setVideoUrl(`${API_ROOT}/video/${selection.unique_id}?token=${token}`);
        }
      } catch (error) {
        console.error("❌ SELECTION: Error fetching presigned URL:", error);
        console.log("🔄 SELECTION: Falling back to proxied access");
        setVideoUrl(`${API_ROOT}/video/${selection.unique_id}?token=${token}`);
      }
    };
    
    fetchVideoUrl();
  }, [selection?.unique_id, token]);

  // Handle video loading errors
  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.error("Video loading error:", e);
    const videoElement = e.currentTarget;
    console.log("Video error details:", videoElement.error);

    // If S3 URL failed, try getting a new presigned URL
    if (videoElement.src.includes('amazonaws.com')) {
      console.log("🔍 SELECTION DEBUG: S3 presigned URL failed, getting new presigned URL");
      
      const fetchNewPresignedUrl = async () => {
        try {
          const response = await fetch(`${API_ROOT}/video/${selection?.unique_id}/url?token=${token}&retry=${Date.now()}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.presigned_url) {
              console.log("✅ SELECTION: Got new presigned URL for retry");
              setVideoUrl(data.presigned_url);
              return;
            }
          }
        } catch (error) {
          console.error("Error fetching new presigned URL:", error);
        }
        
        // Fallback to proxied access
        console.log("🔄 SELECTION: Falling back to proxied access");
        setVideoUrl(`${API_ROOT}/video/${selection?.unique_id}?token=${token}&t=${Date.now()}`);
      };

      fetchNewPresignedUrl();
      return;
    }

    // For non-S3 URLs, retry with proxied access
    if (videoRef.current && token && selection?.unique_id) {
      const newUrl = `${API_ROOT}/video/${selection.unique_id}?token=${token}&t=${Date.now()}`;
      console.log("Retrying video load with URL:", newUrl);
      setVideoUrl(newUrl);
    }
  };

  // Handle video loading success
  const handleVideoLoad = () => {
    console.log("Video loaded successfully");
  };

  useEffect(() => {
    if (!selection_id) {
      console.error("No selection_id provided");
      return;
    }

    try {
      const data = localStorage.getItem(`selection_${selection_id}`);
      console.log("Retrieved selection data:", data); // Debug log

      if (!data) {
        console.error("No data found for selection_id:", selection_id);
        return;
      }

      const parsed = JSON.parse(data);
      console.log("Parsed selection data:", parsed); // Debug log

      // Validate the required fields
      if (!parsed.unique_id || !parsed.campaignName || !Array.isArray(parsed.selectedAspects)) {
        console.error("Invalid selection data structure:", parsed);
        return;
      }

      setSelection(parsed);
      setComment(parsed.comment || "");
    } catch (e) {
      console.error("Error parsing selection data:", e);
    }
  }, [selection_id]);

  // Save comment to localStorage on change
  useEffect(() => {
    if (selection && selection.selectionId) {
      const updated = { ...selection, comment };
      localStorage.setItem(`selection_${selection.selectionId}`, JSON.stringify(updated));
    }
  }, [comment, selection]);

  const handleShareViaEmail = () => {
    if (!email) {
      alert("Please enter an email address");
      return;
    }

    // Get the current URL for the selection page
    const selectionUrl = `${window.location.origin}/selection/${selection_id}`;

    // Create email template
    const subject = `BrandComply Analysis: ${selection.campaignName || 'Video Analysis'}`;
    
    // Create body with selected aspects
    let body = `Hello,\n\nHere is the BrandComply analysis for ${selection.campaignName || 'the video'}:\n\n`;
    
    // First, identify non-compliant aspects
    const nonCompliantAspects = selection.selectedAspects.filter((aspect: any) => !aspect.complies);
    
    if (nonCompliantAspects.length > 0) {
      body += "⚠️ The video does not comply with the following requirements:\n\n";
      nonCompliantAspects.forEach((aspect: any) => {
        const emoji = aspect.cat === 'visual' ? '👁️' : '👂';
        const type = aspect.typ === 'dos' ? 'DO' : 'DON\'T';
        body += `${emoji} ${type}: ${aspect.input}\n`;
      });
      body += "\n";
    }
    
    // Then list all selected aspects
    body += "All Selected Aspects:\n";
    selection.selectedAspects.forEach((aspect: any) => {
      const emoji = aspect.cat === 'visual' ? '👁️' : '👂';
      const type = aspect.typ === 'dos' ? 'DO' : 'DON\'T';
      const status = aspect.complies ? '✅' : '❌';
      body += `${emoji} ${type}: ${aspect.input} ${status}\n`;
    });

    // Add comment if exists
    if (comment) {
      body += `\nComment:\n${comment}\n`;
    }

    // Add analysis link
    body += `\nView Full Analysis: ${selectionUrl}\n`;

    // Create mailto link
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open email client
    window.location.href = mailtoLink;
  };

  const handleLogout = () => {
    // Clear the token from localStorage
    localStorage.removeItem("token");
    // Redirect to auth page
    navigate("/auth");
  };

  if (!selection) return <div className="p-8 text-center text-rose-600">Selection not found or is corrupted.</div>;

  const { campaignName, selectedAspects, analysis } = selection || {};

  if (!Array.isArray(selectedAspects) || typeof analysis !== 'object' || !analysis) {
    return <div className="p-8 text-center text-rose-600">Selection data is incomplete or malformed.</div>;
  }

  // Helper function to format field values
  const formatFieldValue = (value: any) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "✅" : "❌";
    if (typeof value === "number") return value;
    if (Array.isArray(value)) return value.join(", ");
    return value.toString();
  };

  // Field labels for display
  const fieldLabels: Record<string, string> = {
    custom_brand_identification: "Brand Identified",
    custom_brand_proportion: "Brand Proportion",
    custom_brand_first_appearance: "Brand First Appearance",
    custom_product_detection: "Product Detected",
    custom_product_proportion: "Product Proportion",
    custom_product_first_appearance: "Product First Appearance",
    visible_brands: "Visible Brands",
    contains_sensitive_content: "Contains Sensitive Content",
    total_processing_time: "Total Processing Time",
  };

  return (
    <div style={{ background: PALETTE.gunmetal }} className="min-h-screen px-4 py-8">
      <SEO
        title="Selection Results"
        description="Review your BrandComply content selection results and compliance scores."
        path={`/selection-result/${selection_id}`}
      />
      <div className="w-full px-4 mx-auto max-w-6xl">
        <div className="flex justify-end mb-6">
          <div className="flex gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="flex items-center gap-2"
              style={{ borderColor: PALETTE.paleViolet, color: PALETTE.paleViolet }}
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2"
              style={{ borderColor: PALETTE.orange, color: PALETTE.orange }}
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
        <div className="space-y-6">
          <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>Selected Aspects</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Video Player */}
            <Card style={{ background: PALETTE.brightSnow }} className="p-6">
              <h3 className="text-lg font-medium mb-4" style={{ color: PALETTE.gunmetal }}>Video</h3>
              {videoUrl && (
                <video 
                  ref={videoRef}
                  key={videoUrl}
                  controls 
                  className="w-full rounded-lg"
                  style={{ maxHeight: '80vh' }}
                  crossOrigin="anonymous"
                  onError={handleVideoError}
                  onLoad={handleVideoLoad}
                >
                  <source 
                    src={videoUrl}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              )}
            </Card>
            {/* Selected Aspects (Middle) */}
            <Card style={{ background: PALETTE.brightSnow }} className="p-6">
              <h3 className="text-lg font-medium mb-4" style={{ color: PALETTE.gunmetal }}>Selected Aspects</h3>
              <div className="mb-2"><span className="font-medium">Campaign Name:</span> {campaignName || "-"}</div>
              <div className="space-y-4">
                {/* Display selected aspects */}
                {selectedAspects.length === 0 && (!selection.selectedFields || selection.selectedFields.length === 0) ? (
                  <div className="text-slate-400">No aspects selected.</div>
                ) : (
                  <>
                    {/* Display DOs and DON'Ts */}
                    {selectedAspects.map((aspect: any, idx: number) => (
                      <div className="flex items-center" key={idx}>
                        <span className="w-[120px] font-medium">
                          {aspect.cat === 'visual' ? '👁️' : '👂'} {aspect.typ === 'dos' ? "DO" : "DON'T"}
                        </span>
                        <span className="w-[220px] font-medium ml-2">{aspect.input}</span>
                        <span className="ml-4">{typeof aspect.complies === 'boolean' ? (aspect.complies ? '✅' : '❌') : '-'}</span>
                      </div>
                    ))}
                    
                    {/* Display selected fields */}
                    {selection.selectedFields?.map((field: any) => (
                      <div className="flex items-center" key={field.key}>
                        <span className="w-[220px] font-medium">{field.label}</span>
                        <span className="ml-4">{formatFieldValue(field.value)}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </Card>
            {/* Comment and Email Share (Right) */}
            <div style={{ background: PALETTE.brightSnow }} className="space-y-6 p-6">
              <Card className="p-6">
                <h3 className="text-lg font-medium mb-4" style={{ color: PALETTE.gunmetal }}>Comment</h3>
                <textarea
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
                  rows={4}
                  placeholder="Enter your comments here..."
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                />
              </Card>
              <Card className="p-6">
                <h3 className="text-lg font-medium mb-4" style={{ color: PALETTE.gunmetal }}>Share via Email</h3>
                <div className="space-y-4">
                  <Input
                    type="email"
                    placeholder="Enter recipient's email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full"
                  />
                  <Button
                    onClick={handleShareViaEmail}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Share via Email
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectionResult; 